<template>
  <div class="pb-3">
    <organism-attachment-management
      v-if="filterData"
      :userList="userList"
      :tagList="tagList"
      :filter="filterData"
      :attachmentFields="attachmentFields"
      :attachmentList="attachmentList"
      @onFetchingData="onFetchingData"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @deleteAttachment="deleteAttachment"
    ></organism-attachment-management>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getAttachments,
  getSharedTags,
  deleteContractAttachmentsById,
  getAllUserList,
} from "@/api/api-module1"
import OrganismAttachmentManagement from "@/components/organisms/portal/internal/attachment/OrganismAttachmentManagement.vue"
import { mapActions, mapGetters } from "vuex"
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"

export default {
  name: "PortalAttachmentManagement",
  components: { OrganismAttachmentManagement },
  mixins: [mixins, mixinsDateTime, mixinsSiteHelper, mixinsObjectHelper],
  data() {
    return {
      userList: [],
      tagList: [],

      attachmentFields: [
        {
          key: "idc",
          label: "NTDC ID",
        },
        {
          key: "attachment",
          label: "Attachment",
        },
        {
          key: "description",
          label: "Description",
        },
        {
          key: "tag",
          label: "Tag",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "upload",
          label: "Upload",
        },
        {
          key: "update",
          label: "Last Updated",
        },
        {
          key: "action",
          label: "",
        },
      ],
      attachmentList: [],
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getInternalAttachmentFilter"]),

    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),
  },
  watch: {
    async siteSetting() {
      await this.getAttachmentList()
    },
  },
  async mounted() {
    await Promise.all([
      this.getUserList(),
      this.getTagList(),
      this.getAttachmentList(),
    ])
  },
  methods: {
    ...mapActions("appFilter", ["setInternalAttachmentFilter"]),

    async getUserList() {
      const params = {
        siteIds: this.siteOptions.map((site) => site.value),
      }
      await getAllUserList(params).then((response) => {
        this.userList = response.map((user) => ({
          username: user.username,
          name: `${user.firstName} ${user.lastName}`,
        }))
      })
    },

    async getTagList() {
      const isInternal = true
      await getSharedTags(null, isInternal).then((tags) => {
        this.tagList = tags.map((tag) => tag.key)
      })
    },

    async getAttachmentList() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = await this.filterDecoration()
      await getAttachments(filterData)
        .then((response) => {
          this.filterData.totalCount = response.totalCount
          this.setInternalAttachmentFilter({
            ...this.getInternalAttachmentFilter,
            totalCount: response.totalCount,
          })

          this.attachmentList = response.data.map((item) => {
            return {
              id: item.attachmentId,
              idc: {
                ntdcId: item.idcId.toString().padStart(8, "0"),
                idcId: item.idcId,
                name: item.customerName,
              },
              attachment: {
                title: item.title,
                file: {
                  name: item.fileName,
                  originalFileName: item.originFileName,
                },
              },
              description: item.description,
              tag: item.tags,
              upload: {
                uploadBy: item.uploadedBy, // map name with users list
                uploadAt: this.setDateTimeFormat(
                  item.uploadedAt,
                  "dd/MM/yyyy HH:mm:ss"
                ),
              },
              update: {
                updateBy: item.updatedBy, // map name with users list
                updateAt: this.setDateTimeFormat(
                  item.updatedAt,
                  "dd/MM/yyyy HH:mm:ss"
                ),
              },
              link: item.link,
            }
          })
        })
        .finally(() => {
          this.showLoading(false)
        })
    },

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getInternalAttachmentFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    filterDecoration() {
      const filter = {
        PageSize: this.filterData.perPage,
        PageNumber: this.filterData.pageNumber,
        contractId: Number(this.filterData.idcId) || undefined,
        title: this.filterData.title || undefined,
        uploadedBy: this.filterData.uploadedBy?.length
          ? this.filterData.uploadedBy.map((item) => item.username)
          : undefined,
        updatedBy: this.filterData.updatedBy?.length
          ? this.filterData.updatedBy.map((item) => item.username)
          : undefined,
        tags: this.filterData.tag?.length ? this.filterData.tag : undefined,
        description: this.filterData.description || undefined,
        filename: this.filterData.filename || undefined,
        isEnableDate: undefined,
        totalCount: undefined,
        startDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.startDate)
          : undefined,
        endDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.endDate)
          : undefined,
        siteIds: this.siteOptions.map((site) => site.value),
      }
      return filter
    },

    onUpdateFilterData(filter) {
      this.setInternalAttachmentFilter({ ...filter, pageNumber: 1 })
      this.getAttachmentList()
    },

    onFetchingData() {
      this.getAttachmentList()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        idcId: "",
        title: "",
        filename: "",
        uploadBy: [],
        updateBy: [],
        description: "",
        tag: [],
        uploadDate: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isEnableDate: false,
        pageNumber: 1,
        totalCount: 0,
        startDate: DateTime.now().startOf("day").toISO(),
        endDate: DateTime.now().toISO(),
        perPage: 10,
      }

      this.setInternalAttachmentFilter(defaultFilter)

      this.getAttachmentList()
    },

    async deleteAttachment(id, idcId) {
      await deleteContractAttachmentsById(id, idcId)
        .then((response) => {
          if (response) {
            this.toastSuccess("Deleted Attachment Successfully")
            this.getAttachmentList()
          } else {
            this.toastError("Delete Attachment Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getInternalAttachmentFilter,
        pageNumber: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setInternalAttachmentFilter(updatedFilter)
      this.getAttachmentList()
    },
  },
}
</script>

<style lang="scss" scoped></style>
